import React, { useCallback, useEffect, useRef, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid, Typography, OutlinedInput } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import Alert from '@material-ui/lab/Alert'
import Inputmask from 'inputmask'
import { useDispatch, useSelector } from 'react-redux'
import { Loader } from '@refera/ui-web'

import Select from '_components/select'
import Textfield from '_components/textfield'
import Button from '_components/button'
import useForm from '_hooks/use-form'
import useToggle from '_hooks/use-toggle'
import { refuseBudget, getReasonsToRefusal } from '_modules/budget/actions'
import { getHistoryLogs } from '_modules/history-logs/actions'
import { getBudgetByIdSelector, getReasonsToRefuseSelector } from '_modules/budget/selectors'

import { REASON_TO_REFUSE_BUDGET as REASON_OPTIONS } from '_utils/constants/budget'

import { ConfidentialDataWarning } from '_/components/ConfidentialDataWarning'

import { formatErrorMessage } from '_/views/finance/utils/FormatErrorMessage'
import { useToast } from '_/hooks/use-toast'
import useRolePermission from '_/hooks/use-role-permission'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import { getServiceOrder, getActiveBudget } from '_/modules/service-orders/actions'
import SelectProviderDialog from '../select-provider-dialog'
import { cancelReasonsSelector } from '_/modules/utils/selectors'
import { getReasonsToCancel } from '_/modules/utils/actions'

import useStyles from './styles'

const CANCELLATION_OPTIONS = {
  YES: 'yes',
  NO: 'no',
}

const DO_CANCEL_OPTIONS = [
  { value: CANCELLATION_OPTIONS.YES, label: 'Sim' },
  { value: CANCELLATION_OPTIONS.NO, label: 'Não' },
]

const INITIAL_STATE = {
  reasonForRefusal: '',
  anotherReasonForRefusal: '',
  comfortableAmount: '',
  cancelServiceOrder: false,
  refusalMoreDetails: '',
  serviceOrderCancelDetails: '',
  alsoCancelServiceOrder: '',
  reasonForCancelServiceOrder: '',
}

const initialStateOfErrors = {
  refusalMoreDetails: false,
  serviceOrderCancelDetails: false,
  alsoCancelServiceOrder: false,
  reasonForCancelServiceOrder: false,
  reasonForRefusal: false,
  anotherReasonForRefusal: false,
  comfortableAmount: false,
}

const RejectBudgetForm = ({
  onClose,
  isRevisedProp,
  setSavingBudget = () => {},
  isSavingLoading,
  serviceOrderId,
  budgetId,
  showActionButtons = true,
}) => {
  const reasonToRefusal = useSelector(getReasonsToRefuseSelector('external_budget'))
  const priceRef = useRef(null)
  const dispatch = useDispatch()
  const { showToast } = useToast()
  const { checkIfUserDoesNotPermission, isSAAS } = useRolePermission()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(initialStateOfErrors)

  const errorMessage = 'Campo obrigatório'

  const styles = useStyles()

  const cancelReasons = useSelector(cancelReasonsSelector)
  const budget = useSelector(getBudgetByIdSelector(budgetId))
  const isRevised = isRevisedProp ?? budget?.isRevised ?? false

  const refetchServiceOrder = useCallback(() => {
    dispatch(getServiceOrder(serviceOrderId))
  }, [dispatch, serviceOrderId])

  const {
    values: {
      reasonForRefusal,
      anotherReasonForRefusal,
      comfortableAmount,
      cancelServiceOrder,
      refusalMoreDetails,
      serviceOrderCancelDetails,
      alsoCancelServiceOrder,
      reasonForCancelServiceOrder,
    },
    handleInputChange,
    resetValues,
  } = useForm(INITIAL_STATE)
  const [isProviderDialogOpen, onToggleProviderDialog] = useToggle()

  useEffect(() => {
    if (reasonForRefusal === REASON_OPTIONS.PRICE) {
      Inputmask('9{1,4}.99', { numericInput: true, radixPoint: ',' }).mask(priceRef.current)
    }
  }, [reasonForRefusal])

  useEffect(() => {
    dispatch(getReasonsToRefusal())

    if (!cancelReasons.size) {
      dispatch(getReasonsToCancel({ serviceOrder: serviceOrderId }))
    }
  }, [])

  const setLoadingTrue = useCallback(() => {
    setSavingBudget(prevState => ({
      ...prevState,
      updateBudgetItem: true,
    }))
  }, [setSavingBudget])

  const setLoadingFalse = useCallback(() => {
    setSavingBudget(prevState => ({
      ...prevState,
      updateBudgetItem: false,
    }))
  }, [setSavingBudget])

  const formattedReasonsToRefuseList = useMemo(() => {
    if (reasonToRefusal.length) {
      return reasonToRefusal.map(option => ({ value: option.slug, label: option.reason }))
    }
    return []
  }, [reasonToRefusal])

  const onSendClick = useCallback(
    event => {
      event.preventDefault()
      const checkFields = [{ refusalMoreDetails }, { alsoCancelServiceOrder }, { reasonForRefusal }]

      if (alsoCancelServiceOrder === 'yes') {
        if (
          checkIfUserDoesNotPermission(
            PERMISSIONS.CANCEL_REACTIVATE_SERVICE_ORDER,
            PERMISSIONS_ACTIONS.ADD,
            'Você indicou que também quer cancelar o chamado, mas você não tem permissão para isto.'
          )
        ) {
          return null
        }
        checkFields.push({ serviceOrderCancelDetails }, { reasonForCancelServiceOrder })
        if (reasonForRefusal === REASON_OPTIONS.PRICE) checkFields.push({ comfortableAmount })
        if (reasonForRefusal === REASON_OPTIONS.OTHER) checkFields.push({ anotherReasonForRefusal })
      }

      const fieldsError = checkFields.filter(list =>
        Object.values(list).some(field => field === null || field === '' || field === 'undefined')
      )

      const changeFieldsError = fieldsError.reduce(
        (errors, fieldName) => ({ ...errors, [Object.keys(fieldName)]: true }),
        {}
      )

      if (fieldsError.length > 0) {
        return setError(oldFields => ({ ...oldFields, ...changeFieldsError }))
      }

      const payload = {
        reasonForRefusal,
        cancelServiceOrder: false,
        refusalMoreDetails,
        serviceOrderCancelDetails,
        alsoCancelServiceOrder,
        reasonForCancelServiceOrder,
        ignoreRevision: !isRevised,
      }

      if (reasonForRefusal === REASON_OPTIONS.OTHER) {
        payload.anotherReasonForRefusal = anotherReasonForRefusal
      } else if (reasonForRefusal === REASON_OPTIONS.PRICE) {
        payload.comfortableAmount = Number(comfortableAmount)
      }

      if (alsoCancelServiceOrder === 'yes') {
        payload.cancelServiceOrder = true
        payload.serviceOrderCancelDetails = serviceOrderCancelDetails
      } else if (alsoCancelServiceOrder === 'no') {
        payload.cancelServiceOrder = false
      }

      payload.refusalMoreDetails = refusalMoreDetails
      payload.alsoCancelServiceOrder = alsoCancelServiceOrder
      payload.reasonForCancelServiceOrder = reasonForCancelServiceOrder
      setLoadingTrue()
      setIsSubmitting(true)

      dispatch(
        refuseBudget({
          serviceOrderId,
          budgetId,
          payload,
        })
      )
        .then(() => {
          refetchServiceOrder()
          dispatch(getHistoryLogs(serviceOrderId))
          dispatch(getActiveBudget(serviceOrderId))
          onRefuseSuccess()
          resetValues()
        })
        .catch(err => {
          const message = formatErrorMessage(err)
          showToast({ type: 'error', message })
        })
        .finally(() => {
          setLoadingFalse()
          setIsSubmitting(false)
        })
      return null
    },
    [
      anotherReasonForRefusal,
      budgetId,
      cancelServiceOrder,
      comfortableAmount,
      dispatch,
      reasonForRefusal,
      serviceOrderId,
      refusalMoreDetails,
      serviceOrderCancelDetails,
      alsoCancelServiceOrder,
      reasonForCancelServiceOrder,
      isRevised,
      checkIfUserDoesNotPermission,
      setLoadingTrue,
      refetchServiceOrder,
      setLoadingFalse,
      resetValues,
      onClose,
    ]
  )

  const onRefuseSuccess = useCallback(() => {
    onClose({ reason: 'success' }) // reason is for kanban
    setError(initialStateOfErrors)
  }, [onClose])

  const onCloseProviderDialog = useCallback(() => {
    onToggleProviderDialog()
    setError(initialStateOfErrors)
  }, [onToggleProviderDialog])

  const onSendCounterProposalSuccess = useCallback(() => {
    if (!budget.requestedChangeServiceProvider) {
      onToggleProviderDialog()
      return
    }
    onToggleProviderDialog()
    onClose()
  }, [budget.requestedChangeServiceProvider, onClose, onToggleProviderDialog])

  const warningMessage = useMemo(() => {
    const message = isSAAS
      ? 'Ao responder Não no campo Deseja cancelar também o chamado, o chamado voltará para a etapa “Alocar prestador” onde você deverá selecionar um novo prestador para um novo orçamento.'
      : 'Ao responder Não no campo Deseja cancelar também o chamado, a Refera providenciará um novo orçamento a um novo prestador.'

    return (
      <>
        <b>Atenção</b>
        <br />
        {message}
      </>
    )
  }, [isSAAS])

  return (
    <form id="rejectBudgetForm" onSubmit={onSendClick}>
      <Loader open={isSubmitting || isSavingLoading} hasBackdrop />
      <Grid className={styles.content}>
        <Select
          IconComponent={ExpandMoreIcon}
          label="Selecione o motivo"
          options={formattedReasonsToRefuseList}
          onChange={handleInputChange}
          name="reasonForRefusal"
          value={reasonForRefusal}
          emptyValue=""
          defaultValue=""
          className={styles.select}
          error={error.reasonForRefusal && !reasonForRefusal}
        />
        {error.reasonForRefusal && !reasonForRefusal && (
          <Typography className={styles.errorText}>{errorMessage}</Typography>
        )}
        {reasonForRefusal === REASON_OPTIONS.OTHER && (
          <Textfield
            value={anotherReasonForRefusal}
            onChange={handleInputChange}
            name="anotherReasonForRefusal"
            label="Qual?"
            style={{ display: 'none' }}
            className={styles.input}
          />
        )}
        {reasonForRefusal === REASON_OPTIONS.PRICE && (
          <Textfield
            value={comfortableAmount}
            onChange={handleInputChange}
            name="comfortableAmount"
            label="Qual valor você estaria confortável em pagar?"
            className={styles.input}
            inputRef={priceRef}
            InputProps={{
              startAdornment: 'R$ ',
            }}
          />
        )}
        <Typography className={styles.textTitle}>Mais detalhes</Typography>
        <OutlinedInput
          multiline
          className={styles.textInputN}
          placeholder="Mais detalhes."
          name="refusalMoreDetails"
          value={refusalMoreDetails}
          onChange={handleInputChange}
          required
          error={error.refusalMoreDetails && !refusalMoreDetails}
        />
        {error.refusalMoreDetails && !refusalMoreDetails && (
          <Typography className={styles.errorText}>{errorMessage}</Typography>
        )}
        <ConfidentialDataWarning style={{ marginTop: 8 }} />
        <Select
          IconComponent={ExpandMoreIcon}
          label="Deseja cancelar também o chamado?"
          options={DO_CANCEL_OPTIONS}
          onChange={handleInputChange}
          name="alsoCancelServiceOrder"
          value={alsoCancelServiceOrder}
          emptyValue=""
          defaultValue=""
          className={styles.select}
          required
          error={error.alsoCancelServiceOrder && !alsoCancelServiceOrder}
        />

        {error.alsoCancelServiceOrder && !alsoCancelServiceOrder && (
          <Typography className={styles.errorText}>{errorMessage}</Typography>
        )}
        {alsoCancelServiceOrder === 'yes' && (
          <>
            <Typography className={styles.textTitle}>
              <Alert severity="warning">
                <b>Atenção</b>
                <br />
                Ao responder Sim no campo Deseja cancelar também o chamado, a solicitação do serviço
                de manutenção será cancelada e não haverá mais solicitação de novos orçamentos a
                outros prestadores.
              </Alert>
            </Typography>

            <Select
              label="Selecione o motivo do cancelamento"
              options={cancelReasons?.toArray()}
              name="reasonForCancelServiceOrder"
              value={reasonForCancelServiceOrder}
              onChange={handleInputChange}
              required
              error={error.reasonForCancelServiceOrder && !reasonForCancelServiceOrder}
            />
            {error.reasonForCancelServiceOrder && !reasonForCancelServiceOrder && (
              <Typography className={styles.errorText}>{errorMessage}</Typography>
            )}
            <Typography className={styles.textTitle}>Mais detalhes do cancelamento</Typography>
            <OutlinedInput
              multiline
              inputProps={{ maxLength: 1000 }}
              className={styles.textInput}
              placeholder="Mais detalhes."
              name="serviceOrderCancelDetails"
              value={serviceOrderCancelDetails}
              onChange={handleInputChange}
              required
              error={error.serviceOrderCancelDetails && !serviceOrderCancelDetails}
            />
            {error.serviceOrderCancelDetails && !serviceOrderCancelDetails && (
              <Typography className={styles.errorText}>{errorMessage}</Typography>
            )}
            <ConfidentialDataWarning style={{ marginTop: 8 }} />
          </>
        )}
        {alsoCancelServiceOrder === 'no' && (
          <Typography className={styles.textTitle}>
            <Alert severity="warning">{warningMessage}</Alert>
          </Typography>
        )}
      </Grid>
      {showActionButtons && (
        <Grid className={styles.action}>
          <Button
            variant="contained"
            color="primary"
            disabled={isSavingLoading}
            type="submit"
            form="rejectBudgetForm"
          >
            Enviar
          </Button>
          <Button variant="outlined" color="primary" onClick={onClose}>
            Cancelar
          </Button>
        </Grid>
      )}

      {isProviderDialogOpen && (
        <SelectProviderDialog
          isOpen
          onClose={onCloseProviderDialog}
          onSearchForAnotherProvider={onClose}
          serviceOrderId={serviceOrderId}
          budgetId={budgetId}
          onSendCounterProposalSuccess={onSendCounterProposalSuccess}
        />
      )}
    </form>
  )
}

RejectBudgetForm.propTypes = {
  onClose: PropTypes.func.isRequired,
  setSavingBudget: PropTypes.func.isRequired,
  isSavingLoading: PropTypes.bool,
}

RejectBudgetForm.defaultProps = {
  isSavingLoading: false,
}

export default React.memo(RejectBudgetForm)
